import React, { useState } from 'react';
import { useAddress } from '../contexts/AddressContext';
import type { Address } from '../contexts/AddressContext';
import { useToast } from '../contexts/ToastContext';
import { 
  validateAddress, 
  validateCity, 
  validateState, 
  validateZipCode, 
  sanitizeInput 
} from '../utils/validation';

interface AddressFormData {
  label: string;
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country?: string;
  isDefault: boolean;
}

interface AddressBookProps {
  onAddressSelect?: (address: Address) => void;
  showSelection?: boolean;
}

const AddressBook: React.FC<AddressBookProps> = ({ 
  onAddressSelect, 
  showSelection = false 
}) => {
  const { showToast } = useToast();
  const { 
    addresses, 
    selectedAddressId, 
    addAddress, 
    updateAddress, 
    deleteAddress, 
    setDefaultAddress, 
    selectAddress,
    isLoading 
  } = useAddress();

  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingAddress, setEditingAddress] = useState<Address | null>(null);
  const [formData, setFormData] = useState<AddressFormData>({
    label: 'Home',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'India',
    isDefault: addresses.length === 0,
  });
  const [errors, setErrors] = useState<Partial<AddressFormData>>({});

  const resetForm = () => {
    setFormData({
      label: 'Home',
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'India',
      isDefault: addresses.length === 0,
    });
    setErrors({});
    setIsAddingNew(false);
    setEditingAddress(null);
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<AddressFormData> = {};

    // Validate label
    if (!formData.label || formData.label.trim() === '') {
      newErrors.label = 'Address label is required';
    }

    // Validate street
    const streetValidation = validateAddress(formData.street);
    if (!streetValidation.isValid) {
      newErrors.street = streetValidation.error;
    }

    // Validate city
    const cityValidation = validateCity(formData.city);
    if (!cityValidation.isValid) {
      newErrors.city = cityValidation.error;
    }

    // Validate state
    const stateValidation = validateState(formData.state);
    if (!stateValidation.isValid) {
      newErrors.state = stateValidation.error;
    }

    // Validate ZIP code
    const zipValidation = validateZipCode(formData.zipCode);
    if (!zipValidation.isValid) {
      newErrors.zipCode = zipValidation.error;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    let sanitizedValue = value;
    
    // Sanitize input based on field type
    if (type !== 'checkbox') {
      sanitizedValue = sanitizeInput(value);
      
      // Additional sanitization for specific fields
      if (name === 'phone') {
        sanitizedValue = sanitizedValue.replace(/\D/g, '').slice(0, 10);
      } else if (name === 'zipCode') {
        sanitizedValue = sanitizedValue.replace(/\D/g, '').slice(0, 6);
      } else if (name === 'name' || name === 'city' || name === 'state') {
        // Only allow letters, spaces, hyphens, and apostrophes for names and places
        sanitizedValue = sanitizedValue.replace(/[^a-zA-Z\s'-]/g, '').slice(0, 50);
      } else if (name === 'address') {
        sanitizedValue = sanitizedValue.slice(0, 200);
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : sanitizedValue
    }));

    // Clear error when user starts typing
    if (errors[name as keyof AddressFormData]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      const sanitizedFormData = {
        ...formData,
        label: sanitizeInput(formData.label).trim(),
        street: sanitizeInput(formData.street).trim(),
        city: sanitizeInput(formData.city).trim(),
        state: sanitizeInput(formData.state).trim(),
        zipCode: sanitizeInput(formData.zipCode).replace(/\D/g, ''),
        country: formData.country || 'India',
      };

      if (editingAddress) {
        updateAddress({ ...editingAddress, ...sanitizedFormData });
        showToast('Address updated successfully!', 'success');
      } else {
        addAddress(sanitizedFormData);
        showToast('Address added successfully!', 'success');
      }
      
      resetForm();
    } catch {
      setErrors({ label: 'Failed to save address. Please try again.' });
      showToast('Failed to save address. Please try again.', 'error');
    }
  };

  const handleEdit = (address: Address) => {
    setFormData({
      label: address.label,
      street: address.street,
      city: address.city,
      state: address.state,
      zipCode: address.zipCode,
      country: address.country || 'India',
      isDefault: address.isDefault,
    });
    setEditingAddress(address);
    setIsAddingNew(true);
  };

  const handleAddressSelection = (address: Address) => {
    selectAddress(address.id);
    onAddressSelect?.(address);
  };

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-500 mx-auto mb-2"></div>
        <p className="text-gray-600">Loading addresses...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Address List */}
      {addresses.length > 0 && (
        <div className="space-y-3">
          {addresses.map((address) => (
            <div 
              key={address.id} 
              className={`border rounded-lg p-4 ${
                showSelection && selectedAddressId === address.id 
                  ? 'border-accent-500 bg-accent-50' 
                  : 'border-gray-200'
              } ${showSelection ? 'cursor-pointer hover:border-accent-300' : ''}`}
              onClick={() => showSelection && handleAddressSelection(address)}
            >
              <div className="flex justify-between items-start">
                <div className="flex-grow">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-gray-900">{address.label}</h3>
                    {address.isDefault && (
                      <span className="text-xs px-2 py-1 bg-accent-600 text-white rounded-full font-medium">
                        Default
                      </span>
                    )}
                    {showSelection && selectedAddressId === address.id && (
                      <span className="text-xs px-2 py-1 bg-green-500 text-white rounded-full font-medium">
                        Selected
                      </span>
                    )}
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    {address.street}, {address.city}, {address.state} - {address.zipCode}
                  </p>
                </div>
                
                {!showSelection && (
                  <div className="flex gap-2 ml-4">
                    <button
                      type="button"
                      onClick={() => handleEdit(address)}
                      className="text-accent-500 hover:text-accent-600 text-sm font-medium"
                    >
                      Edit
                    </button>
                    {!address.isDefault && (
                      <button
                        type="button"
                        onClick={() => {
                          setDefaultAddress(address.id);
                          showToast('Default address updated!', 'success');
                        }}
                        className="text-blue-500 hover:text-blue-600 text-sm font-medium"
                      >
                        Set Default
                      </button>
                    )}
                    {addresses.length > 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm('Are you sure you want to delete this address?')) {
                            deleteAddress(address.id);
                            showToast('Address deleted successfully!', 'success');
                          }
                        }}
                        className="text-red-500 hover:text-red-600 text-sm font-medium"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add New Address Button */}
      {!isAddingNew && (
        <button
          type="button"
          onClick={() => setIsAddingNew(true)}
          className="w-full bg-accent-600 text-white hover:bg-accent-700 hover:shadow-lg hover:shadow-accent-500/25 transition-all duration-300 hover:scale-[1.02] rounded-xl py-4 px-6 font-semibold flex items-center justify-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add New Address
        </button>
      )}

      {/* Add/Edit Address Form */}
      {isAddingNew && (
        <div className="border rounded-lg p-6 bg-gray-50">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {editingAddress ? 'Edit Address' : 'Add New Address'}
            </h3>
            <button
              type="button"
              onClick={resetForm}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label htmlFor="label" className="block text-sm font-medium text-gray-700 mb-1">
                  Address Label *
                </label>
                <select
                  id="label"
                  name="label"
                  value={formData.label}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-accent-500 focus:border-accent-500 ${
                    errors.label ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="Home">Home</option>
                  <option value="Work">Work</option>
                  <option value="Other">Other</option>
                </select>
                {errors.label && <p className="text-red-500 text-sm mt-1">{errors.label}</p>}
              </div>
            </div>

            <div>
              <label htmlFor="street" className="block text-sm font-medium text-gray-700 mb-1">
                Street Address *
              </label>
              <input
                type="text"
                id="street"
                name="street"
                value={formData.street}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-accent-500 focus:border-accent-500 ${
                  errors.street ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="House no, Building, Street, Area"
              />
              {errors.street && <p className="text-red-500 text-sm mt-1">{errors.street}</p>}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                  City *
                </label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-accent-500 focus:border-accent-500 ${
                    errors.city ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city}</p>}
              </div>

              <div>
                <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">
                  State *
                </label>
                <input
                  type="text"
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-accent-500 focus:border-accent-500 ${
                    errors.state ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.state && <p className="text-red-500 text-sm mt-1">{errors.state}</p>}
              </div>

              <div>
                <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 mb-1">
                  ZIP Code *
                </label>
                <input
                  type="text"
                  id="zipCode"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-accent-500 focus:border-accent-500 ${
                    errors.zipCode ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.zipCode && <p className="text-red-500 text-sm mt-1">{errors.zipCode}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4">
              <div className="flex items-center pt-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="isDefault"
                    checked={formData.isDefault}
                    onChange={handleInputChange}
                    className="rounded border-gray-300 text-accent-500 focus:ring-accent-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Set as default address</span>
                </label>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={handleSubmit}
                className="bg-accent-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-accent-700 hover:shadow-lg hover:shadow-accent-500/25 transition-all duration-300 hover:scale-105"
              >
                {editingAddress ? 'Update Address' : 'Save Address'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="bg-neutral-200 text-neutral-700 px-6 py-3 rounded-xl font-semibold hover:bg-neutral-300 transition-all duration-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {addresses.length === 0 && !isAddingNew && (
        <div className="text-center py-12 text-gray-500">
          <div className="text-4xl mb-4">📍</div>
          <h3 className="text-lg font-medium mb-2">No addresses saved</h3>
          <p className="text-sm">Add your first address to get started</p>
        </div>
      )}
    </div>
  );
};

export default AddressBook;